import {
  CarouselItemId,
  ICustomFormCarouselItem,
} from '@homex/se-widget-flow-types';
import { useRef } from 'react';
import { useSelector } from 'react-redux';

import { reportWidgetInteraction } from 'actions';
import { Carousel } from 'components/Carousel';
import { CarouselItem } from 'components/CarouselItem';
import { CarouselItemAddressCorrectionBottom } from 'components/CarouselItemAddressCorrectionBottom';
import { CarouselItemAddressNotFoundBottom } from 'components/CarouselItemAddressNotFoundBottom';
import { CarouselItemAddressRepairBottom } from 'components/CarouselItemAddressRepairBottom';
import { CarouselItemAddressRepairCorrectionBottom } from 'components/CarouselItemAddressRepairCorrectionBottom';
import { CarouselItemAddressRepairNotFoundBottom } from 'components/CarouselItemAddressRepairNotFoundBottom';
import { CarouselItemAddressSalesBottom } from 'components/CarouselItemAddressSalesBottom';
import { CarouselItemAvailablePartnersBottom } from 'components/CarouselItemAvailablePartnersBottom';
import { CarouselItemConfirmFinishRepairBottom } from 'components/CarouselItemConfirmFinishRepairBottom';
import { CarouselItemConfirmFinishRepairTop } from 'components/CarouselItemConfirmFinishRepairTop';
import { CarouselItemConfirmFinishSalesBottom } from 'components/CarouselItemConfirmFinishSalesBottom';
import { CarouselItemConfirmFinishSalesTop } from 'components/CarouselItemConfirmFinishSalesTop';
import {
  CarouselItemContactInfoRepairBottom,
  CarouselItemContactInfoSalesBottom,
} from 'components/CarouselItemContactInfoBottom';
import { CarouselItemCustomFormBottom } from 'components/CarouselItemCustomFormBottom';
import { CarouselItemCustomFormTop } from 'components/CarouselItemCustomFormTop';
import { CarouselItemCustomerLocationsBottom } from 'components/CarouselItemCustomerLocationsBottom';
import { CarouselItemCustomerLocationsRecoveredBottom } from 'components/CarouselItemCustomerLocationsRecoveredBottom';
import { CarouselItemCustomerLookupBottom } from 'components/CarouselItemCustomerLookupBottom';
import { CarouselItemIssueDetailsBottom } from 'components/CarouselItemIssueDetailsBottom';
import { CarouselItemIssueDetailsTop } from 'components/CarouselItemIssueDetailsTop';
import { CarouselItemIssueSpecifierBottom } from 'components/CarouselItemIssueSpecifierBottom';
import { CarouselItemMediaUploadBottom } from 'components/CarouselItemMediaUploadBottom';
import { CarouselItemMediaUploadDetail } from 'components/CarouselItemMediaUploadDetail';
import { CarouselItemRepairCommentsBottom } from 'components/CarouselItemRepairCommentsBottom';
import { CarouselItemRepairSummaryBottom } from 'components/CarouselItemRepairSummaryBottom';
import { CarouselItemRepairSummaryTop } from 'components/CarouselItemRepairSummaryTop';
import { CarouselItemSalesSummaryBottom } from 'components/CarouselItemSalesSummaryBottom';
import { CarouselItemSalesSummaryTop } from 'components/CarouselItemSalesSummaryTop';
import { CarouselItemScheduleCalendarBottom } from 'components/CarouselItemScheduleCalendarBottom';
import { CarouselItemScheduleCalendarTop } from 'components/CarouselItemScheduleCalendarTop';
import { CarouselItemScheduleEmergencyBottom } from 'components/CarouselItemScheduleEmergencyBottom';
import { CarouselItemScheduleMaintenanceExpiredBottom } from 'components/CarouselItemScheduleMaintenanceExpiredBottom';
import { CarouselItemScheduleMaintenanceExpiredTop } from 'components/CarouselItemScheduleMaintenanceExpiredTop';
import { CarouselItemScheduleMaintenanceNoTimeSlotsBottom } from 'components/CarouselItemScheduleMaintenanceNoTimeSlotsBottom';
import { CarouselItemScheduleMaintenanceNoTimeSlotsTop } from 'components/CarouselItemScheduleMaintenanceNoTimeSlotsTop';
import { CarouselItemSchedulePreferenceBottom } from 'components/CarouselItemSchedulePreferenceBottom';
import { CarouselItemSelectServiceTypeBottom } from 'components/CarouselItemSelectServiceTypeBottom';
import { CarouselItemSelectServiceTypeTop } from 'components/CarouselItemSelectServiceTypeTop';
import { CarouselItemServiceAreaBottom } from 'components/CarouselItemServiceAreaBottom';
import { CarouselItemSystemCommentsBottom } from 'components/CarouselItemSystemCommentsBottom';
import {
  CarouselItemSystemEnergyBottom,
  CarouselItemSystemEquipmentBottom,
} from 'components/CarouselItemSystemSpecs';
import { CarouselItemTop } from 'components/CarouselItemTop';
import { CarouselNav } from 'components/CarouselNav';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselBackgroundStyle,
  CarouselItemRefMap,
  CarouselPosition,
  CarouselReduxReducer,
  ICarouselItemGrouping,
  IIssueStackItem,
  IState,
} from 'typings';
import isIssueDetailsItemSkipped from 'utils/isIssueDetailsItemSkipped';
import isIssueImmediatelyRedirected from 'utils/isIssueImmediatelyRedirected';

const idToComponentMap: { [TKey in CarouselItemId]?: ICarouselItemGrouping } = {
  [CarouselItemId.ServiceType]: {
    componentBottom: <CarouselItemSelectServiceTypeBottom />,
    componentTop: <CarouselItemSelectServiceTypeTop />,
    id: CarouselItemId.ServiceType,
  },
  [CarouselItemId.IssueSpecifier]: {
    componentBottom: <CarouselItemIssueSpecifierBottom />,
    componentTop: <CarouselItemTop headerPhraseId="ISSUE_SPECIFIER.HEADER" />,
    id: CarouselItemId.IssueSpecifier,
  },
  [CarouselItemId.MediaUpload]: {
    componentBottom: <CarouselItemMediaUploadBottom />,
    componentTop: <CarouselItemTop headerPhraseId="MEDIA_UPLOAD.HEADER" />,
    id: CarouselItemId.MediaUpload,
  },
  [CarouselItemId.MediaDetail]: {
    componentBottom: <CarouselItemMediaUploadDetail />,
    componentTop: <CarouselItemTop headerPhraseId="MEDIA_UPLOAD.HEADER" />,
    id: CarouselItemId.MediaDetail,
  },
  [CarouselItemId.RepairComments]: {
    componentBottom: <CarouselItemRepairCommentsBottom />,
    componentTop: <CarouselItemTop headerPhraseId="REPAIR_COMMENTS.HEADER" />,
    id: CarouselItemId.RepairComments,
  },
  [CarouselItemId.CustomerLookup]: {
    componentBottom: <CarouselItemCustomerLookupBottom />,
    componentTop: <CarouselItemTop headerPhraseId="CUSTOMER_LOOKUP.HEADER" />,
    id: CarouselItemId.CustomerLookup,
  },
  [CarouselItemId.CustomerLocations]: {
    componentBottom: <CarouselItemCustomerLocationsBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="CUSTOMER_LOCATIONS.HEADER" />
    ),
    id: CarouselItemId.CustomerLocations,
  },
  [CarouselItemId.CustomerLocationsRecovered]: {
    componentBottom: <CarouselItemCustomerLocationsRecoveredBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="CUSTOMER_LOCATIONS_RECOVERED.HEADER" />
    ),
    id: CarouselItemId.CustomerLocationsRecovered,
  },
  [CarouselItemId.RepairContactInfo]: {
    componentBottom: <CarouselItemContactInfoRepairBottom />,
    componentTop: <CarouselItemTop headerPhraseId="CONTACT_INFO.HEADER" />,
    id: CarouselItemId.RepairContactInfo,
  },
  [CarouselItemId.ServiceAreaRepair]: {
    componentBottom: (
      <CarouselItemServiceAreaBottom
        destinationItemId={CarouselItemId.RepairAddress}
        successPhraseId="SERVICE_AREA_REPAIR.SUCCESS.HTML"
      />
    ),
    componentTop: <CarouselItemTop headerPhraseId="SERVICE_AREA.HEADER" />,
    id: CarouselItemId.ServiceAreaRepair,
  },
  [CarouselItemId.RepairAddress]: {
    componentBottom: <CarouselItemAddressRepairBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SALES_ADDRESS.HEADER" />,
    id: CarouselItemId.RepairAddress,
  },
  [CarouselItemId.RepairAddressCorrection]: {
    componentBottom: <CarouselItemAddressRepairCorrectionBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SALES_ADDRESS.HEADER" />,
    id: CarouselItemId.RepairAddressCorrection,
  },
  [CarouselItemId.RepairAddressNotFound]: {
    componentBottom: <CarouselItemAddressRepairNotFoundBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="SALES_ADDRESS_NOT_FOUND.HEADER" />
    ),
    id: CarouselItemId.RepairAddressNotFound,
  },
  [CarouselItemId.SchedulePreference]: {
    componentBottom: <CarouselItemSchedulePreferenceBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="SCHEDULE_CALENDAR_REPAIR.HEADER" />
    ),
    id: CarouselItemId.SchedulePreference,
  },
  [CarouselItemId.ScheduleCalendarRepair]: {
    componentBottom: <CarouselItemScheduleCalendarBottom />,
    componentTop: (
      <CarouselItemScheduleCalendarTop headerPhraseId="SCHEDULE_CALENDAR_REPAIR.HEADER" />
    ),
    id: CarouselItemId.ScheduleCalendarRepair,
  },
  [CarouselItemId.ScheduleMaintenanceNoTimeSlots]: {
    componentBottom: <CarouselItemScheduleMaintenanceNoTimeSlotsBottom />,
    componentTop: <CarouselItemScheduleMaintenanceNoTimeSlotsTop />,
    id: CarouselItemId.ScheduleMaintenanceNoTimeSlots,
  },
  [CarouselItemId.AvailablePartners]: {
    componentBottom: <CarouselItemAvailablePartnersBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="AVAILABLE_PARTNERS.HEADER" />
    ),
    id: CarouselItemId.AvailablePartners,
  },
  [CarouselItemId.RepairSummary]: {
    componentBottom: <CarouselItemRepairSummaryBottom />,
    componentTop: <CarouselItemRepairSummaryTop />,
    id: CarouselItemId.RepairSummary,
  },
  [CarouselItemId.ConfirmFinishRepair]: {
    componentBottom: <CarouselItemConfirmFinishRepairBottom />,
    componentTop: <CarouselItemConfirmFinishRepairTop />,
    id: CarouselItemId.ConfirmFinishRepair,
  },
  [CarouselItemId.ScheduleMaintenanceExpired]: {
    componentBottom: <CarouselItemScheduleMaintenanceExpiredBottom />,
    componentTop: <CarouselItemScheduleMaintenanceExpiredTop />,
    id: CarouselItemId.ScheduleMaintenanceExpired,
  },
  [CarouselItemId.ContactInfo]: {
    componentBottom: <CarouselItemContactInfoSalesBottom />,
    componentTop: <CarouselItemTop headerPhraseId="CONTACT_INFO.HEADER" />,
    id: CarouselItemId.ContactInfo,
  },
  [CarouselItemId.ServiceAreaSales]: {
    componentBottom: <CarouselItemServiceAreaBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SERVICE_AREA.HEADER" />,
    id: CarouselItemId.ServiceAreaSales,
  },
  [CarouselItemId.SalesAddress]: {
    componentBottom: <CarouselItemAddressSalesBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SALES_ADDRESS.HEADER" />,
    id: CarouselItemId.SalesAddress,
  },
  [CarouselItemId.SalesAddressCorrection]: {
    componentBottom: <CarouselItemAddressCorrectionBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SALES_ADDRESS.HEADER" />,
    id: CarouselItemId.SalesAddressCorrection,
  },
  [CarouselItemId.SalesAddressNotFound]: {
    componentBottom: (
      <CarouselItemAddressNotFoundBottom
        parentCarouselItemId={CarouselItemId.SalesAddressCorrection}
      />
    ),
    componentTop: (
      <CarouselItemTop headerPhraseId="SALES_ADDRESS_NOT_FOUND.HEADER" />
    ),
    id: CarouselItemId.SalesAddressNotFound,
  },
  [CarouselItemId.ScheduleEmergency]: {
    componentBottom: <CarouselItemScheduleEmergencyBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="SCHEDULE_EMERGENCY.HEADER" />
    ),
    id: CarouselItemId.ScheduleEmergency,
  },
  [CarouselItemId.SchedulePreferenceSales]: {
    componentBottom: <CarouselItemSchedulePreferenceBottom />,
    componentTop: (
      <CarouselItemTop headerPhraseId="SCHEDULE_CALENDAR_SALES.HEADER" />
    ),
    id: CarouselItemId.SchedulePreferenceSales,
  },
  [CarouselItemId.SystemEquipment]: {
    componentBottom: <CarouselItemSystemEquipmentBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SYSTEM_EQUIPMENT.HEADER" />,
    id: CarouselItemId.SystemEquipment,
  },
  [CarouselItemId.SystemEnergy]: {
    componentBottom: <CarouselItemSystemEnergyBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SYSTEM_ENERGY.HEADER" />,
    id: CarouselItemId.SystemEnergy,
  },
  [CarouselItemId.SystemComments]: {
    componentBottom: <CarouselItemSystemCommentsBottom />,
    componentTop: <CarouselItemTop headerPhraseId="SYSTEM_COMMENTS.HEADER" />,
    id: CarouselItemId.SystemComments,
  },
  [CarouselItemId.SalesSummary]: {
    componentBottom: <CarouselItemSalesSummaryBottom />,
    componentTop: <CarouselItemSalesSummaryTop />,
    id: CarouselItemId.SalesSummary,
  },
  [CarouselItemId.ConfirmFinishSales]: {
    componentBottom: <CarouselItemConfirmFinishSalesBottom />,
    componentTop: <CarouselItemConfirmFinishSalesTop />,
    id: CarouselItemId.ConfirmFinishSales,
  },
};

export const ServiceCarouselGroup = () => {
  const carouselBottomRef = useRef<HTMLDivElement>(null);
  // We store all of our CarouselItem liRefs in object maps then pass them into
  // the Carousel so that their height may be measured and animated. This
  // strategy of handling refs between components is discussed more here:
  // https://reactjs.org/docs/refs-and-the-dom.html
  const carouselItemRefsBottom = useRef<CarouselItemRefMap>({});
  const carouselItemRefsTop = useRef<CarouselItemRefMap>({});
  const { flow, history, issueStack } = useSelector((state: IState) => ({
    flow: state.client.flow,
    history: state.serviceCarousel.history,
    issueStack: state.userInput.issueStack,
  }));

  const dispatch = useThunkDispatch();

  const issueDetailsItemGroupings = issueStack
    .filter(
      ({ issue }) =>
        !isIssueDetailsItemSkipped(issue) &&
        !isIssueImmediatelyRedirected(issue),
    )
    .map((issueStackItem: IIssueStackItem) => {
      return {
        componentBottom: (
          <CarouselItemIssueDetailsBottom issueStackItem={issueStackItem} />
        ),
        componentTop: (
          <CarouselItemIssueDetailsTop issueStackItem={issueStackItem} />
        ),
        id: `${CarouselItemId.IssueDetails}-${issueStackItem.issue.id}` as CarouselItemId,
      };
    });

  const customFormCarouselItems = (flow?.navSteps ?? [])
    .map((ns) => ns.carouselItems)
    .flat()
    .filter((ci): ci is ICustomFormCarouselItem => {
      return ci.id.startsWith(CarouselItemId.CustomForm);
    });
  const customFormItemGroupings =
    customFormCarouselItems.map((customFormCarouselItem) => {
      return {
        componentBottom: (
          <CarouselItemCustomFormBottom carouselItem={customFormCarouselItem} />
        ),
        componentTop: (
          <CarouselItemCustomFormTop carouselItem={customFormCarouselItem} />
        ),
        id: `${customFormCarouselItem.id}` as CarouselItemId,
      };
    }) ?? [];

  const componentFlow = history.reduce(
    (filtered: Array<ICarouselItemGrouping>, id) => {
      const isIssueDetailsItem = id.startsWith(CarouselItemId.IssueDetails);
      const isCustomFormItem = id.startsWith(CarouselItemId.CustomForm);
      const item = isIssueDetailsItem
        ? issueDetailsItemGroupings.find(({ id: itemId }) => {
            return itemId === id;
          })
        : isCustomFormItem
        ? customFormItemGroupings.find(({ id: itemId }) => {
            return itemId === id;
          })
        : idToComponentMap[id];

      if (item) {
        filtered.push(item);
      }

      return filtered;
    },
    [],
  );

  const handleWidgetInteraction = () => dispatch(reportWidgetInteraction());

  return (
    <div onClick={handleWidgetInteraction}>
      <Carousel
        backgroundStyle={CarouselBackgroundStyle.GRADIENT_EXPANDED}
        carouselItemRefs={carouselItemRefsTop}
        position={CarouselPosition.TOP}
        reduxReducer={CarouselReduxReducer.SERVICE}
      >
        {componentFlow.map(({ componentTop, id }, index) => (
          <CarouselItem
            id={id}
            index={index}
            key={`${id}-Top`}
            reduxReducer={CarouselReduxReducer.SERVICE}
            ref={(ref) => (carouselItemRefsTop.current[id] = ref)}
          >
            {componentTop}
          </CarouselItem>
        ))}
      </Carousel>
      <CarouselNav carouselBottomRef={carouselBottomRef} />
      <Carousel
        backgroundStyle={CarouselBackgroundStyle.SOLID}
        carouselItemRefs={carouselItemRefsBottom}
        position={CarouselPosition.BOTTOM}
        reduxReducer={CarouselReduxReducer.SERVICE}
        ref={carouselBottomRef}
      >
        {componentFlow.map(({ componentBottom, id }, index) => (
          <CarouselItem
            id={id}
            index={index}
            key={`${id}-Bottom`}
            reduxReducer={CarouselReduxReducer.SERVICE}
            ref={(ref) => (carouselItemRefsBottom.current[id] = ref)}
          >
            {componentBottom}
          </CarouselItem>
        ))}
      </Carousel>
    </div>
  );
};
